/**
 * @author Charles Blais
 */


import xml2js from 'xml2js';


export class Event {
  /**
   * Constructor
   *
   * @constructor
   * @param {Object} data - event object as returned by xml2js decoding
   */
  constructor(data) {
    this._data = data;
  }
  /**
   * The eventID can be coded in several ways under the publicID attribute.
   * In this case, we decode the IRIS or SC3 eventID format.
   *
   *   1. IRIS: url/query?eventid=[eventid]
   *   2. SC3: url/[eventid]
   *
   * @return {string} event id
   */
  get eventid() {
    const splitPattern = this._data.$.publicID.indexOf('eventid=') >= 0 ? 'eventid=' : '/';
    const id = this._data.$.publicID.split(splitPattern);
    return id[id.length - 1];
  }
  /**
   * Getter
   * @return {string} datetime
   */
  get datetime() { return this._data.origin[0].time[0].value[0]; }

  /**
   * Getter
   * @return {number} latitude
   */
  get latitude() { return parseFloat(this._data.origin[0].latitude[0].value[0]); }

  /**
   * Getter
   * @return {number} lnngitude
   */
  get longitude() { return parseFloat(this._data.origin[0].longitude[0].value[0]); }

  /**
   * Getter
   * @return {number} depth
   */
  get depth() {
    return this._data.origin[0].depth ? parseFloat(this._data.origin[0].depth[0].value[0]) : null;
  }

  /**
   * Getter
   * @return {number} magnitude
   */
  get magnitude() {
    const value = parseFloat(this._data.magnitude[0].mag[0].value[0]);
    const truncatedValue = Math.floor(value * 10) / 10
    return truncatedValue;
  }

  /**
   * Getter
   * @return {string} magnitude type
   */
  get magnitudeType() {
    return (this._data.magnitude[0].type) ? this._data.magnitude[0].type[0] : null;
  }

  /**
   * Getter
   * @return {string} event type
   */
  get eventType() {
    if (!(this._data.type?.[0])) return null;
    const eventType = this._data.type?.[0]
    switch (eventType) {
      case 'fluid injection':
        return 'industry-related';
      case 'induced or triggered event':
        return 'industry-related';
      case 'rock burst':
        return 'mining-induced';
      default:
        return eventType;
    }
  }


  /**
   * Getter
   * @return {string} typeCertainty
   */
  get typeCertainty() {
    return (this._data.typeCertainty?.[0]) ? this._data.typeCertainty[0] : null;
  }

  /**
   * Getter
   * @return {string} description
   */
  get description() {
    const desc = this._data.description.find(desc => {
      return desc.type[0] === 'earthquake name';
    });
    return (desc) ? desc.text[0] : '';
  }

  /**
   * Alternative version of get description with language specific
   * @param {str} lang 
   */
  getDescription(lang = 'en') {
    const subdescr = this.description.split('/');
    // An odd number of slashes means the middle one separates english/french text
    const mid = subdescr.length / 2;
    let desc = (subdescr.length % 2 === 0)
      ? (lang === 'fr' ? subdescr.slice(mid).join('/') : subdescr.slice(0, mid).join('/'))
      : this.description;

    // Step 1: Split by comma (assuming first segment may contain type/certainty text)
    const parts = desc.split(',')?.map(p => p.trim());

    // Step 2: Define keywords
    const keywords = [
      'suspected', 'earthquake', 'mining explosion', 'mining event', 'quarry blast', 'blast', 'road cut',
      'industrial explosion', 'mining-induced', 'industry-related',
      'reservoir loading', 'landslide', 'soupconné', 'soupçonné', 'séisme', 'explosion minière', 'dynamitage', 'dynamitage de carrière',
      'travaux routiers', 'explosion industrielle', 'événement minier', 'évènement minier', `événement lié à l'industrie`,
      'lié à l\'industrie', 'mise en eau de réservoir', 'glissement de terrain'
    ];

    // Step 3: Check first part for any keyword
    const firstPart = parts?.[0].toLowerCase();
    const foundKeyword = keywords.some(keyword =>
      firstPart?.includes(keyword.toLowerCase())
    );

    // Step 4: Remove first index if it contains a keyword
    if (foundKeyword) {
      parts.shift();
    }

    // Return cleaned description, rejoining any remaining parts
    return parts.join(', ').trim();
  }


  getEventTypeAndCertainty(lang = 'en', eventTypeTranslator, eventCertaintyTranslator) {
    let result;
    if (!this.eventType) return "";

    if (lang === 'fr') {
      let eventTypeFrTranslation, gender;

      let eventTypeFrTranslationObject = eventTypeTranslator?.[this.eventType] || "";
      let eventCertaintyFrTranslation = eventCertaintyTranslator?.[this.typeCertainty] || '';

      if (eventTypeFrTranslationObject) {
        eventTypeFrTranslation = eventTypeFrTranslationObject.fr;
        gender = eventTypeFrTranslationObject.gender;
      } else {
        eventTypeFrTranslation = `${this.eventType} (anglais seulement)`;
        gender = 'm';
      }

      if (eventCertaintyFrTranslation) {
        if (gender === 'f' || gender === 'fp') eventCertaintyFrTranslation += 'e'; // feminine
        if (gender === 'mp' || gender === 'fp') eventCertaintyFrTranslation += 's'; // plural
      }
      result = `${eventTypeFrTranslation} ${eventCertaintyFrTranslation}`.trim();
    } else {
      result = `${this.typeCertainty || ''} ${this.eventType || ''}`.trim();
    }

    return result;
  }

  /**
   * Convert class into an object that is immutable (for redux)
   * @return {Object} event as object
   */
  serialize() {
    const proto = Object.getPrototypeOf(this);
    const getters = Object.getOwnPropertyNames(proto).reduce((accumulator, prop) => {
      const descriptor = Object.getOwnPropertyDescriptor(proto, prop);
      if (typeof descriptor.get === 'function') {
        return Object.assign({}, accumulator, { [prop]: this[prop] });
      }
      return accumulator;
    }, {});
    return getters;
  }
}
/** QuakeML class */
export class QuakeML {
  /**
   * Constructor
   *
   * @constructor
   * @param {Object} data - xml2js QuakeML response
   * @throws Error
   */
  constructor(data) {
    if (!('q:quakeml' in data)) {
      throw new Error('q:quakeml key does not exist in QuakeML, can not convert');
    }
    if (!('eventParameters' in data['q:quakeml'])) {
      throw new Error('eventParameters key does not exist in QuakeML, can not convert');
    }

    // Reduce the data by removing the root
    const eventParameters = data['q:quakeml'].eventParameters[0];

    // Error thrown if there are no even (invalid format)
    if (!('event' in eventParameters)) {
      throw new Error('There are no event in QuakeML');
    }

    // Convert all the events into Event object
    const events = eventParameters.event.map(event => new Event(event));
    this._data = events;
  }

  /**
   * Getter
   * @return {Object[]} events
   */
  get events() { return this._data; }

  /**
   * Convert class into an object that is immutable (for redux)
   * @return {Object} quakeml as object
   */
  serialize() {
    return { events: this.events.map(event => event.serialize()) };
  }
}


export const xml2event = async xml => {
  const data = await xml2js.parseStringPromise(xml)
  if (data == null) return [];
  const qml = new QuakeML(data);
  return qml.events
}
